import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Check, MessageCircle } from 'lucide-react';
import { WHATSAPP_CONTACT_URL } from './content';
import { PLAN_SEGMENTS } from '../../common/planSegments';
import { buildPlanTiers, tierHeadline } from '../../common/planTiers';
import { planDisplayName } from '../../common/planDisplayName';

// Planos da landing: lista os planos visíveis vindos do backend (GET /plans, público,
// sem autenticação). Usa fetch direto (e não o httpClient) para não disparar o
// refresh de token/redirect do interceptor numa página pública.

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const num = new Intl.NumberFormat('pt-BR');

function formatPrice(plan) {
  return brl.format((Number(plan.value_reais) || 0) + (Number(plan.value_cents) || 0) / 100);
}

// Só aceita link https; qualquer outra coisa cai no botão alternativo.
function safePaymentLink(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch (e) {
    return null;
  }
}

function formatMB(mb) {
  if (mb >= 1024) return `${num.format(Math.round((mb / 1024) * 10) / 10)} GB`;
  return `${num.format(mb)} MB`;
}

function PlanCard({ plan, tier }) {
  const paymentLink = safePaymentLink(plan.payment_link_url);
  // Módulos do card: todos, ou só os extras quando o plano herda de um plano-base (planTiers).
  const moduleDetails = tier ? tier.modules : [];

  return (
    <div className="card-glow plan-card">
      <h3>{planDisplayName(plan.name)}</h3>
      {plan.description && <p className="plan-desc">{plan.description}</p>}
      <div className="plan-price">
        <b>{formatPrice(plan)}</b>
        <span>/mês</span>
      </div>

      <ul className="plan-list">
        {plan.ai_tokens > 0 && (
          <li>
            <span className="pi"><Bot className="i" /></span>
            <span><b>{num.format(plan.ai_tokens)}</b> tokens de IA</span>
          </li>
        )}
        {plan.whatsapp_messages > 0 && (
          <li>
            <span className="pi"><MessageCircle className="i" /></span>
            <span><b>{num.format(plan.whatsapp_messages)}</b> mensagens de WhatsApp/mês</span>
          </li>
        )}
        {plan.max_users > 0 && (
          <li><span className="pi"><Check className="i" /></span>Até <b>{num.format(plan.max_users)}</b> usuários</li>
        )}
        {plan.max_data_mb > 0 && (
          <li><span className="pi"><Check className="i" /></span><b>{formatMB(plan.max_data_mb)}</b> de armazenamento</li>
        )}
        {tier?.baseName && (
          <li className="plan-includes">
            <span className="pi"><Check className="i" /></span>
            <span>{tierHeadline(tier)}</span>
          </li>
        )}
        {moduleDetails.map((mod) => (
          <li key={mod.key}>
            <span className="pi"><Check className="i" /></span>
            <span>
              {mod.label}
              {mod.description && <small className="plan-mod-desc">{mod.description}</small>}
            </span>
          </li>
        ))}
      </ul>

      {paymentLink ? (
        <a href={paymentLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary plan-cta">
          Assinar {planDisplayName(plan.name)}
        </a>
      ) : (
        <Link to="/login?modo=cadastro" className="btn btn-ghost plan-cta">Começar grátis</Link>
      )}
    </div>
  );
}

// Card fixo (não é um plano do banco): orienta falar com um representante quando os
// planos listados não atendem o negócio.
function ContactCard() {
  return (
    <div className="card-glow plan-card plan-card-contact">
      <div className="plan-contact-icon"><span className="pi"><MessageCircle className="i" /></span></div>
      <h3>Precisa de algo diferente?</h3>
      <p className="plan-desc">
        Se os planos listados não atendem o seu negócio, fale com um de nossos representantes.
        Montamos uma proposta sob medida para a sua operação.
      </p>
      <ul className="plan-list plan-contact-list">
        <li><span className="pi"><Check className="i" /></span>Mais de uma unidade ou filial</li>
        <li><span className="pi"><Check className="i" /></span>Volume maior de usuários</li>
        <li><span className="pi"><Check className="i" /></span>Integrações e necessidades específicas</li>
        <li><span className="pi"><Check className="i" /></span>Atendimento personalizado</li>
      </ul>
      <a href={WHATSAPP_CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-ghost plan-cta">
        Falar com um representante
      </a>
    </div>
  );
}

export default function PlansSection() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [segment, setSegment] = useState('');

  const load = useCallback((signal) => {
    setStatus('loading');
    fetch(`${process.env.REACT_APP_API_URL}/plans`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setPlans(Array.isArray(data) ? data : []);
        setStatus('ready');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Abas por segmento: só existem segmentos presentes nos planos devolvidos; planos
  // sem segmento ("todos") aparecem em todas as abas.
  const tabs = PLAN_SEGMENTS.filter((s) => plans.some((p) => p.segment === s.value));
  const activeSegment = tabs.some((t) => t.value === segment) ? segment : tabs[0]?.value;
  const visiblePlans = tabs.length > 1
    ? plans.filter((p) => !p.segment || p.segment === activeSegment)
    : plans;
  const tiers = buildPlanTiers(visiblePlans);

  return (
    <section className="plans" id="planos">
      <div className="wrap">
        <div className="section-head reveal">
          <p className="eyebrow">Planos</p>
          <h2>Escolha o plano ideal para o seu negócio</h2>
          <p>Comece grátis por 14 dias e assine quando estiver pronto.</p>
        </div>

        {status === 'loading' && (
          <div className="plans-grid" aria-busy="true" aria-label="Carregando planos">
            {[0, 1, 2].map((i) => <div className="plan-skel" key={i} />)}
          </div>
        )}

        {status === 'error' && (
          <div className="plans-state" role="alert">
            <p>Não foi possível carregar os planos agora.</p>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => load()}>Tentar novamente</button>
          </div>
        )}

        {status === 'ready' && plans.length === 0 && (
          <div className="plans-state">
            <p>Os planos estarão disponíveis em breve. Fale com um consultor para conhecer as condições.</p>
            <a href={WHATSAPP_CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Falar com um consultor</a>
          </div>
        )}

        {status === 'ready' && plans.length > 0 && (
          <>
            {tabs.length > 1 && (
              <div className="plans-tabs" role="tablist" aria-label="Segmento" style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
                {tabs.map((tab) => (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeSegment === tab.value}
                    key={tab.value}
                    className={`btn btn-sm ${activeSegment === tab.value ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setSegment(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            <div className="plans-grid">
              {visiblePlans.map((plan) => <PlanCard plan={plan} tier={tiers.get(plan.id)} key={plan.id} />)}
              <ContactCard />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
